import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useCampaign } from '../context/CampaignContext';
import { t } from '../lib/i18n';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { FactionSelect } from '../components/ui/FactionSelect';
import { IllustrationUpload } from '../components/ui/IllustrationUpload';
import { CreatureStatsEditor } from '../components/lore/CreatureStatsEditor';
import { isGameMaster, getNPCStatusClass, getVisibilityClass, getInitials, cn } from '../lib/utils';
import {
  buildCreatureEntityData,
  createCreatureStatFields,
  getCreatureStatFields,
} from '../lib/creatureStats';
import { toast } from 'sonner';
import {
  Plus, Search, Book, Crown, Swords, Gem, MapPin, Calendar,
  Users, Sparkles, Scroll, Eye, EyeOff, Edit, Trash2, Link2,
  Filter, Tag, Clock, ChevronRight, X, BookOpen, Landmark,
  Shield, Skull, Star, Globe, FileText, History, Loader2,
  Heart, HelpCircle, User
} from 'lucide-react';

const API_URL = '/api';

const NPC_STATUSES = ['alive', 'dead', 'missing', 'hostile', 'ally'];

const STATUS_CONFIG = {
  alive: { icon: Heart, color: 'text-emerald-400', bg: 'bg-emerald-500/15 border-emerald-500/30' },
  dead: { icon: Skull, color: 'text-red-400', bg: 'bg-red-500/15 border-red-500/30' },
  missing: { icon: HelpCircle, color: 'text-amber-400', bg: 'bg-amber-500/15 border-amber-500/30' },
  hostile: { icon: Swords, color: 'text-red-400', bg: 'bg-red-500/15 border-red-500/30' },
  ally: { icon: Shield, color: 'text-blue-400', bg: 'bg-blue-500/15 border-blue-500/30' }
};

// Article type icons and labels
const ARTICLE_TYPES = {
  pnj: { icon: User, label: { fr: 'PNJ', en: 'NPC' }, color: 'bg-teal-500' },
  free: { icon: FileText, label: { fr: 'Libre', en: 'Free' }, color: 'bg-gray-500' },
  faction: { icon: Users, label: { fr: 'Faction', en: 'Faction' }, color: 'bg-purple-500' },
  deity: { icon: Sparkles, label: { fr: 'Divinité', en: 'Deity' }, color: 'bg-yellow-500' },
  historical_figure: { icon: Crown, label: { fr: 'Figure historique', en: 'Historical Figure' }, color: 'bg-amber-600' },
  artifact: { icon: Gem, label: { fr: 'Artefact', en: 'Artifact' }, color: 'bg-cyan-500' },
  location: { icon: MapPin, label: { fr: 'Lieu', en: 'Location' }, color: 'bg-green-500' },
  event: { icon: Calendar, label: { fr: 'Événement', en: 'Event' }, color: 'bg-red-500' },
  spell: { icon: Scroll, label: { fr: 'Sort', en: 'Spell' }, color: 'bg-blue-500' },
  organization: { icon: Shield, label: { fr: 'Organisation', en: 'Organization' }, color: 'bg-indigo-500' },
  concept: { icon: BookOpen, label: { fr: 'Concept', en: 'Concept' }, color: 'bg-pink-500' },
  creature: { icon: Skull, label: { fr: 'Créature', en: 'Creature' }, color: 'bg-orange-500' },
  legend: { icon: Star, label: { fr: 'Légende', en: 'Legend' }, color: 'bg-violet-500' }
};

// Revelation level badges
const REVELATION_LEVELS = {
  hidden: { label: { fr: 'Caché', en: 'Hidden' }, color: 'bg-gray-700', icon: EyeOff },
  rumor: { label: { fr: 'Rumeur', en: 'Rumor' }, color: 'bg-yellow-600', icon: Eye },
  known: { label: { fr: 'Connu', en: 'Known' }, color: 'bg-blue-500', icon: Eye },
  confirmed: { label: { fr: 'Confirmé', en: 'Confirmed' }, color: 'bg-green-500', icon: Eye },
  myth: { label: { fr: 'Mythe/Faux', en: 'Myth/False' }, color: 'bg-purple-600', icon: Star }
};

export default function LorePage() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, language } = useAuth();
  const { currentCampaign } = useCampaign();

  const [articles, setArticles] = useState([]);
  const [npcs, setNpcs] = useState([]);
  const [templates, setTemplates] = useState({});
  const [gameSystem, setGameSystem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [selectedNpc, setSelectedNpc] = useState(null);

  // UI States
  const [createOpen, setCreateOpen] = useState(false);
  const [createMode, setCreateMode] = useState('article'); // 'article' or 'npc'
  const [editOpen, setEditOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState(searchParams.get('type') || 'all');
  const [filterRevelation, setFilterRevelation] = useState('all');
  const [viewMode, setViewMode] = useState('list'); // list, detail

  // NPC creation form state
  const [creatingNpc, setCreatingNpc] = useState(false);
  const [newNpc, setNewNpc] = useState({
    name: '',
    alias: '',
    race: '',
    faction: '',
    role: '',
    public_info: '',
    status: 'alive',
    visibility: 'mj_only'
  });

  // Article form state
  const [newArticle, setNewArticle] = useState({
    title: '',
    article_type: 'free',
    summary: '',
    content: '',
    private_content: '',
    tags: '',
    visibility: 'players',
    revelation_level: 'known',
    sources: '',
    era: '',
    date_in_world: '',
    entity_data: {}
  });

  const userRole = currentCampaign?.members?.find(m => m.user_id === user?.id)?.role;
  const isMJ = isGameMaster(userRole);

  // Fetch articles
  const fetchArticles = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/campaigns/${campaignId}/lore`);
      setArticles(response.data);
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur de chargement' : 'Loading error');
    }
  }, [campaignId, language]);

  // Fetch NPCs
  const fetchNpcs = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/campaigns/${campaignId}/npcs`);
      setNpcs(response.data);
    } catch (error) {
      console.error('Failed to load NPCs');
    }
  }, [campaignId]);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/lore-templates`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to load templates');
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchArticles(), fetchNpcs(), fetchTemplates()]).finally(() => setLoading(false));
  }, [fetchArticles, fetchNpcs, fetchTemplates]);

  useEffect(() => {
    if (!currentCampaign?.system_id) {
      setGameSystem(null);
      return;
    }
    axios.get(`${API_URL}/game-systems/${currentCampaign.system_id}`)
      .then(response => setGameSystem(response.data))
      .catch(() => setGameSystem(null));
  }, [currentCampaign?.system_id]);

  // Handle createFaction redirect from FactionSelect
  useEffect(() => {
    if (location.state?.createFaction) {
      setCreateMode('article');
      setNewArticle(prev => ({
        ...prev,
        article_type: 'faction',
        title: location.state.factionName || ''
      }));
      setCreateOpen(true);
      // Clear the state to avoid re-triggering
      navigate(location.pathname + location.search, { replace: true, state: {} });
    }
  }, [location.state]);

  // Create NPC
  const handleCreateNpc = async (e) => {
    e.preventDefault();
    if (!newNpc.name.trim()) {
      toast.error(language === 'fr' ? 'Nom requis' : 'Name required');
      return;
    }

    setCreatingNpc(true);
    try {
      const response = await axios.post(`${API_URL}/campaigns/${campaignId}/npcs`, newNpc);
      toast.success(t('success_saved', language));
      setCreateOpen(false);
      setNewNpc({
        name: '', alias: '', race: '', faction: '', role: '',
        public_info: '', status: 'alive', visibility: 'mj_only'
      });
      setNpcs(prev => [...prev, response.data]);
      // Navigate to the NPC detail page
      navigate(`/campaigns/${campaignId}/npcs/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error_generic', language));
    } finally {
      setCreatingNpc(false);
    }
  };

  // Create article
  const handleCreateArticle = async () => {
    if (!newArticle.title.trim()) {
      toast.error(language === 'fr' ? 'Titre requis' : 'Title required');
      return;
    }

    try {
      const payload = {
        ...newArticle,
        tags: newArticle.tags.split(',').map(t => t.trim()).filter(Boolean),
        sections: templates[newArticle.article_type]?.sections || [],
        entity_data: newArticle.article_type === 'creature' ? newArticle.entity_data : {}
      };

      const response = await axios.post(`${API_URL}/campaigns/${campaignId}/lore`, payload);
      setArticles(prev => [response.data, ...prev]);
      setCreateOpen(false);
      setNewArticle({
        title: '', article_type: 'free', summary: '', content: '', private_content: '',
        tags: '', visibility: 'players', revelation_level: 'known', sources: '', era: '', date_in_world: '',
        entity_data: {}
      });
      toast.success(language === 'fr' ? 'Article créé !' : 'Article created!');

      // Open the new article for editing
      setSelectedArticle(response.data);
      setSelectedNpc(null);
      setViewMode('detail');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Update article
  const handleUpdateArticle = async () => {
    if (!selectedArticle) return;

    try {
      const payload = {
        ...selectedArticle,
        tags: typeof selectedArticle.tags === 'string'
          ? selectedArticle.tags.split(',').map(t => t.trim()).filter(Boolean)
          : selectedArticle.tags
      };

      await axios.patch(`${API_URL}/lore/${selectedArticle.id}`, payload);
      setArticles(prev => prev.map(a => a.id === selectedArticle.id ? selectedArticle : a));
      toast.success(language === 'fr' ? 'Article sauvegardé' : 'Article saved');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Delete article
  const handleDeleteArticle = async (articleId) => {
    if (!window.confirm(language === 'fr' ? 'Supprimer cet article ?' : 'Delete this article?')) return;

    try {
      await axios.delete(`${API_URL}/lore/${articleId}`);
      setArticles(prev => prev.filter(a => a.id !== articleId));
      if (selectedArticle?.id === articleId) {
        setSelectedArticle(null);
        setViewMode('list');
      }
      toast.success(language === 'fr' ? 'Article supprimé' : 'Article deleted');
    } catch (error) {
      toast.error(language === 'fr' ? 'Erreur' : 'Error');
    }
  };

  // Build unified item list: articles + NPCs as pseudo-articles
  const npcItems = npcs.map(npc => ({
    ...npc,
    _isNpc: true,
    _type: 'pnj',
    title: npc.name,
    summary: [npc.alias ? `"${npc.alias}"` : null, npc.race, npc.role].filter(Boolean).join(' · '),
    article_type: 'pnj',
    tags: [npc.faction, npc.status].filter(Boolean),
    revelation_level: npc.visibility === 'mj_only' ? 'hidden' : 'known'
  }));

  // Filter articles
  const filteredArticles = articles.filter(article => {
    if (filterType === 'pnj') return false; // NPCs are handled separately
    if (searchQuery && !article.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !article.summary?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !article.tags?.some(t => t.toLowerCase().includes(searchQuery.toLowerCase()))) {
      return false;
    }
    if (filterType !== 'all' && article.article_type !== filterType) return false;
    if (filterRevelation !== 'all' && article.revelation_level !== filterRevelation) return false;
    return true;
  });

  // Filter NPCs
  const filteredNpcs = npcItems.filter(npc => {
    if (filterType !== 'all' && filterType !== 'pnj') return false;
    if (searchQuery && !npc.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !npc.alias?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !npc.faction?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    return true;
  });

  // Merge into unified list
  const allItems = [...filteredArticles.map(a => ({ ...a, _isNpc: false })), ...filteredNpcs];

  // Group all items by type
  const groupedItems = {};
  // Add NPCs first if any
  if (filteredNpcs.length > 0) {
    groupedItems['pnj'] = filteredNpcs;
  }
  // Then articles by type
  filteredArticles.forEach(article => {
    const type = article.article_type || 'free';
    if (!groupedItems[type]) groupedItems[type] = [];
    groupedItems[type].push({ ...article, _isNpc: false });
  });

  const handleItemClick = (item) => {
    if (item._isNpc) {
      // Navigate to NPC detail page
      navigate(`/campaigns/${campaignId}/npcs/${item.id}`);
    } else {
      setSelectedArticle(item);
      setSelectedNpc(null);
      setViewMode('detail');
    }
  };

  const handleOpenCreate = (mode) => {
    setCreateMode(mode);
    setCreateOpen(true);
  };

  return (
    <MainLayout fluid>
      <div className="flex h-[calc(100vh-4rem)]" data-testid="lore-page">
        {/* Left Panel - Article List */}
        <div className={cn(
          "border-r border-border bg-card flex flex-col transition-all",
          viewMode === 'detail' ? "w-80" : "flex-1 max-w-md"
        )}>
          {/* Header */}
          <div className="p-4 border-b border-border">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-cinzel font-bold text-lg flex items-center gap-2">
                <Book className="h-5 w-5" />
                {language === 'fr' ? 'Encyclopédie' : 'Encyclopedia'}
              </h2>
              {isMJ && (
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" onClick={() => handleOpenCreate('npc')} title={t('npc_create', language)}>
                    <User className="h-4 w-4 mr-1" />
                    {language === 'fr' ? 'PNJ' : 'NPC'}
                  </Button>
                  <Button size="sm" onClick={() => handleOpenCreate('article')} data-testid="create-article-btn">
                    <Plus className="h-4 w-4 mr-1" />
                    {language === 'fr' ? 'Article' : 'Article'}
                  </Button>
                </div>
              )}
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={language === 'fr' ? 'Rechercher...' : 'Search...'}
                className="pl-9"
              />
            </div>

            {/* Filters */}
            <div className="flex gap-2 mt-3">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="flex-1">
                  <SelectValue placeholder={language === 'fr' ? 'Type' : 'Type'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{language === 'fr' ? 'Tous les types' : 'All types'}</SelectItem>
                  {Object.entries(ARTICLE_TYPES).map(([key, type]) => (
                    <SelectItem key={key} value={key}>
                      <span className="flex items-center gap-2">
                        <type.icon className="h-4 w-4" />
                        {type.label[language]}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {isMJ && (
                <Select value={filterRevelation} onValueChange={setFilterRevelation}>
                  <SelectTrigger className="w-32">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{language === 'fr' ? 'Tous' : 'All'}</SelectItem>
                    {Object.entries(REVELATION_LEVELS).map(([key, level]) => (
                      <SelectItem key={key} value={key}>{level.label[language]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Item List */}
          <ScrollArea className="flex-1">
            <div className="p-2">
              {loading ? (
                <div className="space-y-2">
                  {[1, 2, 3].map(i => (
                    <Card key={i} className="animate-pulse">
                      <CardContent className="p-3">
                        <div className="h-4 bg-muted rounded w-3/4 mb-2" />
                        <div className="h-3 bg-muted rounded w-1/2" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : Object.keys(groupedItems).length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Book className="h-12 w-12 mx-auto mb-3 opacity-30" />
                  <p>{language === 'fr' ? 'Aucun résultat' : 'No results'}</p>
                  {isMJ && (
                    <div className="flex flex-col gap-2 mt-4 items-center">
                      <Button variant="outline" size="sm" onClick={() => handleOpenCreate('article')}>
                        <Plus className="h-4 w-4 mr-1" />
                        {language === 'fr' ? 'Créer un article' : 'Create article'}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => handleOpenCreate('npc')}>
                        <User className="h-4 w-4 mr-1" />
                        {language === 'fr' ? 'Créer un PNJ' : 'Create NPC'}
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                Object.entries(groupedItems).map(([type, typeItems]) => {
                  const typeInfo = ARTICLE_TYPES[type] || ARTICLE_TYPES.free;
                  const TypeIcon = typeInfo.icon;

                  return (
                    <div key={type} className="mb-4">
                      <div className="flex items-center gap-2 px-2 py-1 text-sm font-medium text-muted-foreground">
                        <TypeIcon className="h-4 w-4" />
                        {typeInfo.label[language]} ({typeItems.length})
                      </div>
                      <div className="space-y-1">
                        {typeItems.map(item => {
                          const isNpc = item._isNpc;

                          if (isNpc) {
                            // NPC card in the list
                            const statusConf = STATUS_CONFIG[item.status] || STATUS_CONFIG.alive;
                            const StatusIcon = statusConf.icon;
                            return (
                              <Card
                                key={`npc-${item.id}`}
                                className="cursor-pointer transition-all hover:bg-secondary/50"
                                onClick={() => handleItemClick(item)}
                              >
                                <CardContent className="p-3">
                                  <div className="flex items-center gap-2.5">
                                    <Avatar className="h-8 w-8">
                                      <AvatarFallback className="bg-gradient-to-br from-teal-500/20 to-teal-500/5 text-teal-500 font-cinzel text-xs">
                                        {getInitials(item.name)}
                                      </AvatarFallback>
                                    </Avatar>
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        <h3 className="font-medium text-sm truncate">{item.name}</h3>
                                        <span className={cn(
                                          "inline-flex items-center gap-1 text-[10px] px-1.5 py-0 rounded-full border shrink-0",
                                          statusConf.bg
                                        )}>
                                          <StatusIcon className={cn("h-2.5 w-2.5", statusConf.color)} />
                                          <span className={statusConf.color}>
                                            {t(`npc_status_${item.status}`, language)}
                                          </span>
                                        </span>
                                      </div>
                                      {item.summary && (
                                        <p className="text-xs text-muted-foreground truncate mt-0.5">
                                          {item.summary}
                                        </p>
                                      )}
                                    </div>
                                    <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                                  </div>
                                </CardContent>
                              </Card>
                            );
                          }

                          // Regular article card
                          const revelationInfo = REVELATION_LEVELS[item.revelation_level] || REVELATION_LEVELS.known;

                          return (
                            <Card
                              key={item.id}
                              className={cn(
                                "cursor-pointer transition-all hover:bg-secondary/50",
                                selectedArticle?.id === item.id && "ring-2 ring-primary bg-secondary/30"
                              )}
                              onClick={() => handleItemClick(item)}
                            >
                              <CardContent className="p-3">
                                <div className="flex items-start gap-2.5">
                                  {item.image_url && (
                                    <img
                                      src={item.image_url}
                                      alt=""
                                      className="h-9 w-9 rounded-md object-cover shrink-0"
                                    />
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                      <h3 className="font-medium text-sm truncate">{item.title}</h3>
                                      {isMJ && (
                                        <Badge className={cn("text-[10px] shrink-0", revelationInfo.color)}>
                                          {revelationInfo.label[language]}
                                        </Badge>
                                      )}
                                    </div>
                                    {item.summary && (
                                      <p className="text-xs text-muted-foreground truncate mt-0.5">
                                        {item.summary}
                                      </p>
                                    )}
                                  </div>
                                </div>
                                {item.tags?.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {item.tags.slice(0, 3).map((tag, i) => (
                                      <Badge key={i} variant="outline" className="text-[10px] px-1">
                                        {tag}
                                      </Badge>
                                    ))}
                                    {item.tags.length > 3 && (
                                      <Badge variant="outline" className="text-[10px] px-1">
                                        +{item.tags.length - 3}
                                      </Badge>
                                    )}
                                  </div>
                                )}
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Right Panel - Article Detail */}
        {viewMode === 'detail' && selectedArticle && (
          <div className="flex-1 flex flex-col min-w-0">
            {/* Article Header */}
            <div className="p-4 border-b border-border bg-card/50">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <IllustrationUpload
                    imageUrl={selectedArticle.image_url || ''}
                    onImageChange={(url) => setSelectedArticle(prev => ({ ...prev, image_url: url }))}
                    entityType="lore"
                    entityId={selectedArticle.id}
                    campaignId={campaignId}
                    language={language}
                    editable={isMJ}
                    size="md"
                    shape="rounded"
                    className="shrink-0"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {(() => {
                        const TypeIcon = ARTICLE_TYPES[selectedArticle.article_type]?.icon || FileText;
                        return <TypeIcon className="h-5 w-5 text-primary" />;
                      })()}
                      <Badge className={ARTICLE_TYPES[selectedArticle.article_type]?.color || 'bg-gray-500'}>
                        {ARTICLE_TYPES[selectedArticle.article_type]?.label[language] || selectedArticle.article_type}
                      </Badge>
                      {isMJ && (
                        <Badge className={REVELATION_LEVELS[selectedArticle.revelation_level]?.color || 'bg-blue-500'}>
                          {REVELATION_LEVELS[selectedArticle.revelation_level]?.label[language]}
                        </Badge>
                      )}
                    </div>
                    {isMJ ? (
                      <Input
                        value={selectedArticle.title}
                        onChange={(e) => setSelectedArticle(prev => ({ ...prev, title: e.target.value }))}
                        className="text-xl font-cinzel font-bold border-0 p-0 h-auto focus-visible:ring-0"
                        placeholder={language === 'fr' ? 'Titre' : 'Title'}
                      />
                    ) : (
                      <h1 className="text-xl font-cinzel font-bold">{selectedArticle.title}</h1>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {isMJ && (
                    <>
                      <Button variant="outline" size="sm" onClick={handleUpdateArticle}>
                        {language === 'fr' ? 'Sauvegarder' : 'Save'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteArticle(selectedArticle.id)}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => { setSelectedArticle(null); setViewMode('list'); }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Article Content */}
            <ScrollArea className="flex-1">
              <div className="p-6 space-y-6">
                {/* Summary */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    {language === 'fr' ? 'Résumé' : 'Summary'}
                  </Label>
                  {isMJ ? (
                    <Textarea
                      value={selectedArticle.summary || ''}
                      onChange={(e) => setSelectedArticle(prev => ({ ...prev, summary: e.target.value }))}
                      placeholder={language === 'fr' ? 'Résumé court...' : 'Short summary...'}
                      rows={2}
                    />
                  ) : (
                    <p className="text-muted-foreground">{selectedArticle.summary || '-'}</p>
                  )}
                </div>

                {/* Main Content */}
                <div>
                  <Label className="text-sm font-medium mb-2 block">
                    {language === 'fr' ? 'Contenu' : 'Content'}
                  </Label>
                  {isMJ ? (
                    <Textarea
                      value={selectedArticle.content || ''}
                      onChange={(e) => setSelectedArticle(prev => ({ ...prev, content: e.target.value }))}
                      placeholder={language === 'fr' ? 'Contenu détaillé...' : 'Detailed content...'}
                      rows={8}
                      className="min-h-[200px]"
                    />
                  ) : (
                    <div className="prose prose-sm dark:prose-invert max-w-none">
                      {selectedArticle.content?.split('\n').map((p, i) => (
                        <p key={i}>{p}</p>
                      )) || <p className="text-muted-foreground">-</p>}
                    </div>
                  )}
                </div>

                {/* System-agnostic creature stats shared with the combat module */}
                {selectedArticle.article_type === 'creature' && (
                  <div className="rounded-lg border border-orange-500/30 bg-orange-500/5 p-4">
                    <CreatureStatsEditor
                      fields={getCreatureStatFields(
                        selectedArticle.entity_data,
                        createCreatureStatFields(gameSystem?.slug, language)
                      )}
                      onChange={(fields) => setSelectedArticle(prev => ({
                        ...prev,
                        entity_data: buildCreatureEntityData(fields, prev.entity_data),
                      }))}
                      language={language}
                      editable={isMJ}
                      systemName={gameSystem?.name || ''}
                    />
                    <p className="text-xs text-muted-foreground mt-3">
                      {language === 'fr'
                        ? 'Les rôles indiquent comment chaque valeur alimente le tracker de combat. Les autres champs restent entièrement libres.'
                        : 'Roles define how values feed the combat tracker. All other fields remain fully customizable.'}
                    </p>
                  </div>
                )}

                {/* Sections (from template) */}
                {selectedArticle.sections?.length > 0 && (
                  <div className="space-y-4">
                    <Separator />
                    <h3 className="font-semibold">{language === 'fr' ? 'Sections' : 'Sections'}</h3>
                    {selectedArticle.sections
                      .filter(s => isMJ || s.visibility !== 'mj_only')
                      .sort((a, b) => (a.order || 0) - (b.order || 0))
                      .map((section, idx) => (
                        <div key={section.id || idx} className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Label className="text-sm font-medium">{section.title}</Label>
                            {isMJ && section.visibility === 'mj_only' && (
                              <Badge variant="outline" className="text-[10px]">MJ</Badge>
                            )}
                          </div>
                          {isMJ ? (
                            <Textarea
                              value={section.content || ''}
                              onChange={(e) => {
                                const newSections = [...selectedArticle.sections];
                                newSections[idx] = { ...section, content: e.target.value };
                                setSelectedArticle(prev => ({ ...prev, sections: newSections }));
                              }}
                              rows={3}
                            />
                          ) : (
                            <p className="text-sm text-muted-foreground">
                              {section.content || '-'}
                            </p>
                          )}
                        </div>
                      ))}
                  </div>
                )}

                {/* MJ Private Notes */}
                {isMJ && (
                  <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg p-4">
                    <Label className="text-sm font-medium mb-2 flex items-center gap-2">
                      <EyeOff className="h-4 w-4" />
                      {language === 'fr' ? 'Notes MJ (privées)' : 'GM Notes (private)'}
                    </Label>
                    <Textarea
                      value={selectedArticle.private_content || ''}
                      onChange={(e) => setSelectedArticle(prev => ({ ...prev, private_content: e.target.value }))}
                      placeholder={language === 'fr' ? 'Notes secrètes, accroches de scénario...' : 'Secret notes, plot hooks...'}
                      rows={4}
                    />
                  </div>
                )}

                {/* Metadata */}
                {isMJ && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-sm mb-2 block">{language === 'fr' ? 'Tags (séparés par virgule)' : 'Tags (comma separated)'}</Label>
                      <Input
                        value={Array.isArray(selectedArticle.tags) ? selectedArticle.tags.join(', ') : selectedArticle.tags || ''}
                        onChange={(e) => setSelectedArticle(prev => ({ ...prev, tags: e.target.value }))}
                        placeholder="magie, nord, ancien..."
                      />
                    </div>
                    <div>
                      <Label className="text-sm mb-2 block">{language === 'fr' ? 'Niveau de révélation' : 'Revelation level'}</Label>
                      <Select
                        value={selectedArticle.revelation_level}
                        onValueChange={(v) => setSelectedArticle(prev => ({ ...prev, revelation_level: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(REVELATION_LEVELS).map(([key, level]) => (
                            <SelectItem key={key} value={key}>{level.label[language]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-sm mb-2 block">{language === 'fr' ? 'Ère / Période' : 'Era / Period'}</Label>
                      <Input
                        value={selectedArticle.era || ''}
                        onChange={(e) => setSelectedArticle(prev => ({ ...prev, era: e.target.value }))}
                        placeholder={language === 'fr' ? 'Âge des Dragons...' : 'Age of Dragons...'}
                      />
                    </div>
                    <div>
                      <Label className="text-sm mb-2 block">{language === 'fr' ? 'Date dans le monde' : 'In-world date'}</Label>
                      <Input
                        value={selectedArticle.date_in_world || ''}
                        onChange={(e) => setSelectedArticle(prev => ({ ...prev, date_in_world: e.target.value }))}
                        placeholder="1374 DR"
                      />
                    </div>
                  </div>
                )}

                {/* Linked Entities */}
                {selectedArticle.linked_entities?.length > 0 && (
                  <div>
                    <Separator className="my-4" />
                    <Label className="text-sm font-medium mb-3 flex items-center gap-2">
                      <Link2 className="h-4 w-4" />
                      {language === 'fr' ? 'Éléments liés' : 'Linked elements'}
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {selectedArticle.linked_entities.map((link, i) => (
                        <Badge key={i} variant="outline" className="cursor-pointer hover:bg-secondary">
                          {link.name} ({link.type})
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        )}

        {/* Empty state when no article selected */}
        {viewMode === 'list' && (
          <div className="flex-1 flex items-center justify-center bg-muted/20">
            <div className="text-center">
              <Book className="h-16 w-16 mx-auto text-muted-foreground mb-6" />
              <h3 className="font-cinzel text-xl font-bold mb-2">
                {language === 'fr' ? 'Encyclopédie de campagne' : 'Campaign Encyclopedia'}
              </h3>
              <p className="text-muted-foreground mb-6 max-w-md">
                {language === 'fr'
                  ? 'Documentez le lore, les factions, les PNJ et l\'histoire de votre monde'
                  : 'Document the lore, factions, NPCs and history of your world'}
              </p>
              {isMJ && (
                <div className="flex gap-3 justify-center">
                  <Button onClick={() => handleOpenCreate('npc')} variant="outline">
                    <User className="mr-2 h-4 w-4" />
                    {t('npc_create', language)}
                  </Button>
                  <Button onClick={() => handleOpenCreate('article')} className="fantasy-button">
                    <Plus className="mr-2 h-4 w-4" />
                    {language === 'fr' ? 'Nouvel article' : 'New article'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Create Dialog - switches between NPC and Article mode */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-lg">
            {createMode === 'npc' ? (
              /* NPC Creation Form - same as NPCsPage */
              <form onSubmit={handleCreateNpc}>
                <DialogHeader>
                  <DialogTitle className="font-cinzel flex items-center gap-2">
                    <User className="h-5 w-5" />
                    {t('npc_create', language)}
                  </DialogTitle>
                  <DialogDescription>
                    {language === 'fr'
                      ? 'Créez un nouveau personnage non-joueur'
                      : 'Create a new non-player character'}
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4 max-h-[60vh] overflow-y-auto">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="enc-npc-name">{t('npc_name', language)} *</Label>
                      <Input
                        id="enc-npc-name"
                        placeholder={language === 'fr' ? 'Elminster' : 'Elminster'}
                        value={newNpc.name}
                        onChange={(e) => setNewNpc(prev => ({ ...prev, name: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="enc-npc-alias">{t('npc_alias', language)}</Label>
                      <Input
                        id="enc-npc-alias"
                        placeholder={language === 'fr' ? 'Le Sage' : 'The Sage'}
                        value={newNpc.alias}
                        onChange={(e) => setNewNpc(prev => ({ ...prev, alias: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="enc-npc-race">{t('npc_race', language)}</Label>
                      <Input
                        id="enc-npc-race"
                        placeholder={language === 'fr' ? 'Humain' : 'Human'}
                        value={newNpc.race}
                        onChange={(e) => setNewNpc(prev => ({ ...prev, race: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="enc-npc-role">{t('npc_role', language)}</Label>
                      <Input
                        id="enc-npc-role"
                        placeholder={language === 'fr' ? 'Mage, Marchand...' : 'Mage, Merchant...'}
                        value={newNpc.role}
                        onChange={(e) => setNewNpc(prev => ({ ...prev, role: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="enc-npc-faction">{t('npc_faction', language)}</Label>
                    <FactionSelect
                      id="enc-npc-faction"
                      value={newNpc.faction}
                      onChange={(val) => setNewNpc(prev => ({ ...prev, faction: val }))}
                      campaignId={campaignId}
                      language={language}
                      placeholder={language === 'fr' ? 'Les Harpers, La Guilde...' : 'The Harpers, The Guild...'}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="enc-npc-info">{t('npc_public_info', language)}</Label>
                    <Textarea
                      id="enc-npc-info"
                      placeholder={language === 'fr'
                        ? 'Ce que les joueurs savent de ce PNJ...'
                        : 'What players know about this NPC...'}
                      value={newNpc.public_info}
                      onChange={(e) => setNewNpc(prev => ({ ...prev, public_info: e.target.value }))}
                      rows={3}
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>{t('npc_status', language)}</Label>
                      <Select value={newNpc.status} onValueChange={(v) => setNewNpc(prev => ({ ...prev, status: v }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {NPC_STATUSES.map(status => (
                            <SelectItem key={status} value={status}>
                              {t(`npc_status_${status}`, language)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>{t('visibility', language)}</Label>
                      <Select value={newNpc.visibility} onValueChange={(v) => setNewNpc(prev => ({ ...prev, visibility: v }))}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mj_only">{t('visibility_mj_only', language)}</SelectItem>
                          <SelectItem value="players">{t('visibility_players', language)}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>
                    {t('cancel', language)}
                  </Button>
                  <Button type="submit" disabled={creatingNpc} className="fantasy-button">
                    {creatingNpc && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {t('create', language)}
                  </Button>
                </DialogFooter>
              </form>
            ) : (
              /* Article Creation Form */
              <>
                <DialogHeader>
                  <DialogTitle className="font-cinzel flex items-center gap-2">
                    <Book className="h-5 w-5" />
                    {language === 'fr' ? 'Nouvel article' : 'New article'}
                  </DialogTitle>
                  <DialogDescription>
                    {language === 'fr'
                      ? 'Créez un article pour enrichir le lore de votre campagne'
                      : 'Create an article to enrich your campaign lore'}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4 max-h-[65vh] overflow-y-auto pr-1">
                  <div>
                    <Label>{language === 'fr' ? 'Type d\'article' : 'Article type'}</Label>
                    <Select
                      value={newArticle.article_type}
                      onValueChange={(v) => setNewArticle(prev => {
                        if (v !== 'creature') return { ...prev, article_type: v };
                        const fields = getCreatureStatFields(
                          prev.entity_data,
                          createCreatureStatFields(gameSystem?.slug, language)
                        );
                        return {
                          ...prev,
                          article_type: v,
                          entity_data: buildCreatureEntityData(fields, prev.entity_data),
                        };
                      })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(ARTICLE_TYPES).filter(([key]) => key !== 'pnj').map(([key, type]) => (
                          <SelectItem key={key} value={key}>
                            <span className="flex items-center gap-2">
                              <type.icon className="h-4 w-4" />
                              {type.label[language]}
                            </span>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>{language === 'fr' ? 'Titre' : 'Title'}</Label>
                    <Input
                      value={newArticle.title}
                      onChange={(e) => setNewArticle(prev => ({ ...prev, title: e.target.value }))}
                      placeholder={language === 'fr' ? 'Le Culte de l\'Œil Noir' : 'The Cult of the Black Eye'}
                      data-testid="article-title-input"
                    />
                  </div>

                  <div>
                    <Label>{language === 'fr' ? 'Résumé' : 'Summary'}</Label>
                    <Textarea
                      value={newArticle.summary}
                      onChange={(e) => setNewArticle(prev => ({ ...prev, summary: e.target.value }))}
                      placeholder={language === 'fr' ? 'Brève description...' : 'Brief description...'}
                      rows={2}
                    />
                  </div>

                  {newArticle.article_type === 'creature' && (
                    <div className="rounded-lg border border-orange-500/30 bg-orange-500/5 p-3">
                      <CreatureStatsEditor
                        fields={getCreatureStatFields(
                          newArticle.entity_data,
                          createCreatureStatFields(gameSystem?.slug, language)
                        )}
                        onChange={(fields) => setNewArticle(prev => ({
                          ...prev,
                          entity_data: buildCreatureEntityData(fields, prev.entity_data),
                        }))}
                        language={language}
                        systemName={gameSystem?.name || ''}
                      />
                    </div>
                  )}

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label>{language === 'fr' ? 'Visibilité' : 'Visibility'}</Label>
                      <Select
                        value={newArticle.visibility}
                        onValueChange={(v) => setNewArticle(prev => ({ ...prev, visibility: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="players">{language === 'fr' ? 'Joueurs' : 'Players'}</SelectItem>
                          <SelectItem value="mj_only">{language === 'fr' ? 'MJ seulement' : 'GM only'}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>{language === 'fr' ? 'Niveau' : 'Level'}</Label>
                      <Select
                        value={newArticle.revelation_level}
                        onValueChange={(v) => setNewArticle(prev => ({ ...prev, revelation_level: v }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(REVELATION_LEVELS).map(([key, level]) => (
                            <SelectItem key={key} value={key}>{level.label[language]}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label>{language === 'fr' ? 'Tags' : 'Tags'}</Label>
                    <Input
                      value={newArticle.tags}
                      onChange={(e) => setNewArticle(prev => ({ ...prev, tags: e.target.value }))}
                      placeholder="magie, nord, faction..."
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button variant="outline" onClick={() => setCreateOpen(false)}>
                    {language === 'fr' ? 'Annuler' : 'Cancel'}
                  </Button>
                  <Button onClick={handleCreateArticle} disabled={!newArticle.title.trim()} data-testid="create-article-submit">
                    <Plus className="mr-2 h-4 w-4" />
                    {language === 'fr' ? 'Créer' : 'Create'}
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
